import { useEffect, useRef } from 'react';
import { useAuthStore } from '../Auth.store';
import { WebViewAuthService } from '../WebView.auth';
import { Http } from 'src/api';

/**
 * Hook to handle WebView authentication
 *
 * This hook:
 * 1. Detects if we're running in a WebView
 * 2. Sets up a listener for auth messages from React Native
 * 3. Requests authentication if not already authenticated
 * 4. Handles received token
 */
export const useWebViewAuth = () => {
  const { isWebView, isLoggedIn, handleWebViewToken, setWebViewReady } = useAuthStore();
  const webViewAuthRequested = useRef<boolean>(false);

  // Set up listener for auth messages from React Native
  useEffect(() => {
    // Only proceed if we're in a WebView
    if (!isWebView) {
      return;
    }

    // Handle token when received
    const cleanup = WebViewAuthService.listenForAuthMessages((authObj, adopted) => {
      // Write the token only when WebView.auth judged it an upgrade — see
      // auth-token-adoption.logic for why adopting a downgrade destroys a live session.
      //
      // `Http` is the SINGLETON from src/api (the instance every client was constructed
      // with), not the class. This used to be `new Http().setBearer(...)`, which built a
      // whole new axios instance — with its own interceptors and abort signal — mutated its
      // default header and dropped it on the floor. A dead statement that read as
      // load-bearing: the only reason the flow worked is that the request interceptor
      // re-reads `AuthUtils.getJwt()` per request (Http.ts:185-187). useAuth.tsx:56 already
      // uses the singleton for exactly this.
      if (adopted) {
        handleWebViewToken(authObj);
        Http.setBearer(authObj.token);
      }

      // Ready regardless. The handshake DID complete — native answered. Gating this on
      // adoption left a refused reply holding the spinner for the whole recovery budget
      // instead of falling through to the login form.
      setWebViewReady(true);
    });

    return cleanup;
  }, [isWebView, handleWebViewToken, setWebViewReady]);

  // Request auth from native app if needed
  useEffect(() => {
    if (isWebView && !isLoggedIn && !webViewAuthRequested.current) {
      // Mark as requested to prevent duplicate requests
      webViewAuthRequested.current = true;
      // Request auth token from React Native
      WebViewAuthService.requestAuthFromNative();
    }
  }, [isWebView, isLoggedIn]);

  // If already logged in when webview loads, mark as ready immediately
  useEffect(() => {
    if (isWebView && isLoggedIn) {
      setWebViewReady(true);
    }
  }, [isWebView, isLoggedIn, setWebViewReady]);

  return { isWebView };
};
